import { Injectable } from '@angular/core';
import { NavigationCancel, Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { AuthService } from 'app/services/auth.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { MenuService } from "../core/menu/menu.service";
import { filter } from 'rxjs/operators';
import { OAuthService } from 'angular-oauth2-oidc';
import { authConfig } from '../auth.config';
import { BehaviorSubject } from 'rxjs';

@Injectable()
export class LoginService {

  public referrer: String;
  headers: HttpHeaders = new HttpHeaders();

  constructor(
    private router: Router,
    private http: HttpClient,
    private authService: AuthService,
    private menuService: MenuService,
    private oauthService: OAuthService
  ) {
    console.warn('DEBUG: Login service constructed');
    this.configureOAuth();

    this.router.events.pipe(
      filter(e => e instanceof NavigationCancel)
    ).subscribe((e: NavigationCancel) => {
      console.log('Referer: ' + e.url);
      this.referrer = e.url;
    });
  }

  private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  private configureOAuth() {
    this.oauthService.configure(authConfig);
    this.oauthService.setStorage(localStorage); // Persist tokens across tabs/reloads more reliably
    this.oauthService.setupAutomaticSilentRefresh();

    // Subscribe to events for debugging
    this.oauthService.events.subscribe(e => {
      console.warn('DEBUG: oauthEvent', e.type, e);
      if (e.type === 'token_received' || e.type === 'token_refreshed') {
        const newToken = this.oauthService.getAccessToken();
        console.warn('DEBUG: Updating localStorage token', newToken);
        localStorage.setItem('token', newToken);
      }
    });

    // Disable strict validation for debugging potential issuer mismatches
    this.oauthService.strictDiscoveryDocumentValidation = false;

    this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      console.log('loadDiscoveryDocumentAndTryLogin promise resolved');
      sessionStorage.removeItem('auth_in_progress'); // Clear flag in case checkToken isn't called immediately
      this.isDoneLoadingSubject$.next(true);
    }).catch(err => {
      console.error('loadDiscoveryDocumentAndTryLogin failed', err);
      this.isDoneLoadingSubject$.next(true); // Unblock guard generally, though auth failed
    });
  }

  public login() {
    sessionStorage.setItem('auth_in_progress', 'true');
    this.oauthService.initCodeFlow();
  }

  public logout() {
    this.oauthService.revokeTokenAndLogout();
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/logout';
  }

  public get accessToken() {
    return this.oauthService.getAccessToken();
  }
  public get identityClaims() {
    return this.oauthService.getIdentityClaims();
  }

  checkToken() {
    const hasToken = this.oauthService.hasValidAccessToken();
    console.log('checkToken called. hasValidAccessToken:', hasToken);

    if (hasToken) {
      sessionStorage.removeItem('auth_in_progress'); // Clear flag on success
      // Sync legacy storage for compatibility if needed
      if (!localStorage.getItem('token')) {
        localStorage.setItem('token', this.accessToken);
      }

      // Optional: Handle 2FA or other logic here
      if (this.authService.isTwoFaRequired()) {
        this.router.navigate(['/2fa']);
      }

      this.menuService.loadMenuItems();
      return true;
    }

    // Loop protection: Check if we just tried to login
    if (sessionStorage.getItem('auth_in_progress')) {
      console.error('Login failed or incomplete after redirect (auth_in_progress set). Stopping redirect loop.');
      sessionStorage.removeItem('auth_in_progress'); // Clear it so user can try again manually if they want
      return false;
    }

    // Try to login if we have a code, otherwise return false
    console.warn('No valid token found in checkToken. Redirecting to external login');
    this.login();
    return false;
  }

  // Legacy methods kept or commented out as needed
  forgotPassword(data) {
    const params = new HttpParams().set('loginname', data.email);
    return this.http.post(environment.api_base_url + '/accountfunctions/pwdlost', '', { headers: this.headers, params: params });
  }

  resetPassword(uuid, data) {
    const params = new HttpParams().set('password', data.password);
    return this.http.post(environment.api_base_url + '/accountfunctions/activate/' + uuid, '', { headers: this.headers, params: params, responseType: 'text' });
  }

  sendActivationMail(account_id) {
    console.log("activation email start");

    let url = environment.api_base_url + '/accountfunctions/send_activation_mail/' + account_id;
    console.log(url);
    this.http.post(url, '', { headers: this.headers }).subscribe();
    console.log("activation email send");
  }
}
